import { useEffect, useMemo, useState } from "react";
import { Thermometer, Sparkles, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import type { Conditions } from "@/lib/surf";
import type { Surfbreak } from "@/data/surfbreaks";
import {
  allSkillRatings,
  ratingColor,
  recommendBoard,
  recommendBoardForUser,
  type BoardCriteria,
  type RiderStyle,
  type SkillLevel,
} from "@/lib/surf";

// ─── Types ────────────────────────────────────────────────────────────────────

type WetsuitPick = {
  thickness: string;
  extras: string;
  brands: { name: string; model: string; note: string }[];
  blurb: string;
  emoji: string;
};

type WaxPick = {
  type: string;
  baseCoat: string;
  topCoat: string;
  blurb: string;
  emoji: string;
  brands: { name: string; model: string; note: string }[];
};

type Tab = "board" | "wetsuit" | "wax";

const LEVELS: SkillLevel[] = ["Beginner", "Intermediate", "Advanced"];
const STYLES: RiderStyle[] = ["Cruise", "All-round", "Performance"];
const STORAGE_KEY = "barrelcast:board-criteria";

// ─── Wetsuit logic ─────────────────────────────────────────────────────────────

function pickWetsuit(seaTempC: number): WetsuitPick {
  if (seaTempC >= 24) {
    return {
      emoji: "🩳",
      thickness: "Boardshort / Bikini",
      extras: "UV rashguard recommended",
      blurb: "Tropical bath water — no rubber needed. Sun protection is the only concern.",
      brands: [
        { name: "Billabong", model: "Sundays Pro Boardshort", note: "4-way stretch, lightweight" },
        { name: "Rip Curl", model: "Mirage Ultimate", note: "Repel water tech, stays light" },
        { name: "O'Neill", model: "Hyperfreak Hydro", note: "Bonded seams, no chafe" },
      ],
    };
  }
  if (seaTempC >= 22) {
    return {
      emoji: "👕",
      thickness: "Springsuit / Short John (1–2mm)",
      extras: "Or thermal top + boardies",
      blurb: "Warm but a touch fresh on dawnies. A vest or springsuit takes the edge off.",
      brands: [
        { name: "Vissla", model: "7 Seas 2mm Spring", note: "Eco neoprene, soft chest panel" },
        { name: "Patagonia", model: "R1 Lite Yulex Top", note: "Plant-based rubber, very flexy" },
        { name: "Xcel", model: "Comp 2mm Short Sleeve", note: "Race-cut performance fit" },
      ],
    };
  }
  if (seaTempC >= 18) {
    return {
      emoji: "🤿",
      thickness: "3/2mm Full Suit",
      extras: "Barefoot — the workhorse suit",
      blurb: "The all-rounder. Comfortable for most temperate water around the world.",
      brands: [
        { name: "Patagonia", model: "R2 Yulex Front-Zip 3/2", note: "Most flexible 3/2 on the market" },
        { name: "Rip Curl", model: "Flashbomb Fusion 3/2", note: "Dries crazy fast, premium build" },
        { name: "O'Neill", model: "Hyperfreak Comp Zipless 3/2", note: "Lightest 3/2, comp-cut" },
        { name: "Need Essentials", model: "3/2 Steamer", note: "Insane value for a quality suit" },
      ],
    };
  }
  if (seaTempC >= 14) {
    return {
      emoji: "❄️",
      thickness: "4/3mm Full Suit",
      extras: "Optional 3mm booties on cold mornings",
      blurb: "Cool water surf. A good 4/3 + sealed seams keeps you out for hours.",
      brands: [
        { name: "Patagonia", model: "R3 Yulex 4.5/3.5", note: "Warmest plant-based suit going" },
        { name: "Xcel", model: "Drylock 4/3", note: "Drylock seals at wrists/ankles, super warm" },
        { name: "Rip Curl", model: "Flashbomb 4/3 Hooded", note: "Rapid dry, locks in warmth" },
        { name: "Billabong", model: "Furnace Comp 4/3", note: "Graphene lining for extra heat" },
      ],
    };
  }
  if (seaTempC >= 10) {
    return {
      emoji: "🥶",
      thickness: "5/4mm Hooded + 5mm Boots + 3mm Gloves",
      extras: "Full kit — no exposed skin",
      blurb: "Proper cold. Hood and boots are non-negotiable; gloves on the worst days.",
      brands: [
        { name: "Xcel", model: "Drylock 5/4 Hooded", note: "Dry seals, fleece-lined, bombproof" },
        { name: "Rip Curl", model: "Flashbomb Heatseeker 5/4 Hooded", note: "E7 Flash lining" },
        { name: "O'Neill", model: "Psycho Tech 5.5/4 Hooded", note: "TechnoButter neoprene, very flexy" },
        { name: "Patagonia", model: "R4 Yulex Hooded", note: "Sustainable cold-water benchmark" },
      ],
    };
  }
  return {
    emoji: "🧊",
    thickness: "6/5mm Hooded + 7mm Boots + 5mm Lobster Gloves",
    extras: "Earplugs + zinc on cheeks",
    blurb: "Sub-Arctic territory. Limit sessions to ~60 min and warm up properly after.",
    brands: [
      { name: "Xcel", model: "Drylock 6/5 Hooded", note: "Reference standard for ice surf" },
      { name: "O'Neill", model: "Mutant Legend 6/5/4 Hooded", note: "Detachable hood option" },
      { name: "Patagonia", model: "R5 Yulex 6.5/5 Hooded", note: "Plant-based, unreal warmth" },
    ],
  };
}

// ─── Wax logic ─────────────────────────────────────────────────────────────────

function pickWax(seaTempC: number): WaxPick {
  if (seaTempC >= 24) {
    return {
      emoji: "🌴",
      type: "Tropical",
      baseCoat: "Tropical base",
      topCoat: "Tropical top coat",
      blurb: "Hardest wax available — needed to stop melting in warm water. Apply a solid base coat first.",
      brands: [
        { name: "Sex Wax", model: "Mr Zog's Coconut Tropical", note: "The classic — never lets you down" },
        { name: "Sticky Bumps", model: "Original Tropical", note: "Great grip, lasts long in heat" },
        { name: "Matunas", model: "Eco Tropical", note: "Eco-friendly formula, reliable grip" },
      ],
    };
  }
  if (seaTempC >= 19) {
    return {
      emoji: "☀️",
      type: "Warm",
      baseCoat: "Tropical or warm base",
      topCoat: "Warm top coat",
      blurb: "Warm water wax — softer than tropical but firm enough to grip. Popular choice worldwide.",
      brands: [
        { name: "Sex Wax", model: "Mr Zog's Quick Humps Warm", note: "Fast application, solid bumps" },
        { name: "Sticky Bumps", model: "Original Warm", note: "Consistent grip in warm conditions" },
        { name: "Creatures of Leisure", model: "Warm Water Wax", note: "Good value, easy to apply" },
      ],
    };
  }
  if (seaTempC >= 14) {
    return {
      emoji: "🌊",
      type: "Cool",
      baseCoat: "Warm base coat",
      topCoat: "Cool top coat",
      blurb: "Cool water formula stays tacky in temperate conditions. Use a harder base under cooler wax.",
      brands: [
        { name: "Sex Wax", model: "Mr Zog's Cool / Cold", note: "Stays soft and grippy in cool water" },
        { name: "Sticky Bumps", model: "Cool / Cold Original", note: "Great texture, solid bumps" },
        { name: "Matunas", model: "Eco Cool", note: "Plant-based formula, performs well" },
      ],
    };
  }
  return {
    emoji: "🧊",
    type: "Cold",
    baseCoat: "Cold base coat",
    topCoat: "Cold top coat",
    blurb: "Softest wax formula — stays grippy in cold water where harder wax would become slippery.",
    brands: [
      { name: "Sex Wax", model: "Mr Zog's Cold Water", note: "Stays soft below 14°C" },
      { name: "Sticky Bumps", model: "Cold Original", note: "Extra soft formula for cold conditions" },
      { name: "Fjord", model: "Cold Water Wax", note: "Made for Scandinavian surf, built for cold" },
    ],
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function loadCriteria(): BoardCriteria {
  if (typeof window === "undefined")
    return { level: "Intermediate", weightKg: 75, style: "All-round" };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { level: "Intermediate", weightKg: 75, style: "All-round" };
}

// ─── Main component ────────────────────────────────────────────────────────────

export const GearRecommender = ({ c, b }: { c: Conditions; b: Surfbreak }) => {
  const [activeTab, setActiveTab] = useState<Tab>("board");
  const [crit, setCrit] = useState<BoardCriteria>(() => loadCriteria());
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(crit));
    } catch {}
  }, [crit]);

  const sea = c.seaTemp ?? 20;
  const wetsuit = pickWetsuit(sea);
  const wax = pickWax(sea);
  const rec = useMemo(() => recommendBoardForUser(c, b, crit), [c, b, crit]);
  const quickRec = useMemo(() => recommendBoard(c, b), [c, b]);
  const ratings = useMemo(() => allSkillRatings(c, b), [c, b]);

  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: "board", label: "Board", emoji: "🏄" },
    { id: "wetsuit", label: "Wetsuit", emoji: wetsuit.emoji },
    { id: "wax", label: "Wax", emoji: wax.emoji },
  ];

  return (
    <div className="rounded-3xl bg-card/80 backdrop-blur border border-border overflow-hidden">

      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-1">
          <ShoppingBag className="h-3.5 w-3.5" />
          Gear for today · {b.name}
        </div>
        <p className="text-sm text-muted-foreground">
          Sea temp{" "}
          <span className="text-foreground font-semibold">{sea.toFixed(1)}°C</span>
        </p>

        {/* Tab bar */}
        <div className="flex gap-2 mt-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 px-3 text-xs font-semibold transition-all",
                activeTab === t.id
                  ? "bg-gradient-sunset text-accent-foreground"
                  : "bg-background/60 border border-border text-muted-foreground hover:text-foreground"
              )}
            >
              <span>{t.emoji}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── BOARD TAB ── */}
      {activeTab === "board" && (
        <div className="p-6 space-y-4">

          {/* Conditions card */}
          <div className="rounded-2xl border border-border bg-background/60 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
              Conditions say
            </div>
            <h3 className="font-display text-fluid-xl text-safe mt-1 text-foreground leading-tight">
              {quickRec.type}
            </h3>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Length</div>
                <div className="font-bold text-sm">{quickRec.length}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Volume</div>
                <div className="font-bold text-sm">{quickRec.volume}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fins</div>
                <div className="font-bold text-sm">{quickRec.fins}</div>
              </div>
            </div>
            <p className="mt-3 text-foreground/85 text-sm leading-relaxed">{quickRec.why}</p>
          </div>

          {/* Your board result */}
          <div className="rounded-2xl border border-border bg-background/60 p-5">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
                Your board
              </div>
              <span className={cn("text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold", ratingColor(rec.suitability))}>
                {rec.suitability} for you
              </span>
            </div>
            <h4 className="font-display text-fluid-xl text-safe text-foreground leading-tight">{rec.type}</h4>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Length</div>
                <div className="font-bold text-foreground">{rec.length}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Volume</div>
                <div className="font-bold text-foreground">{rec.volumeLiters}L</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fins</div>
                <div className="font-bold text-foreground">{rec.fins}</div>
              </div>
            </div>
            <p className="mt-3 text-foreground/85 text-sm leading-relaxed">{rec.why}</p>
          </div>

          {/* Dial it in trigger */}
          <button
            onClick={() => setSheetOpen(true)}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-all group active:scale-[0.98]"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Dial it in
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-foreground/70">{crit.level} · {crit.style} · {crit.weightKg}kg</span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-primary border border-primary/30 rounded px-1.5 py-0.5">Edit</span>
            </div>
          </button>
        </div>
      )}

      {/* ── BOTTOM SHEET ── */}
      {sheetOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm"
            onClick={() => setSheetOpen(false)}
          />
          {/* Sheet */}
          <div className="fixed inset-x-0 bottom-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-soft max-h-[85vh] overflow-y-auto">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-border" />
            </div>

            <div className="px-6 pb-10 pt-2 space-y-5">
              {/* Sheet header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> Dial it in
                </div>
                <button
                  onClick={() => setSheetOpen(false)}
                  className="text-muted-foreground hover:text-foreground text-xs uppercase tracking-wider font-bold"
                >
                  Done
                </button>
              </div>

              {/* Who should paddle out */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                  Who should paddle out
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {LEVELS.map((l) => (
                    <div key={l} className={cn("rounded-xl border px-2 py-2 text-center", ratingColor(ratings[l]))}>
                      <div className="text-[10px] uppercase tracking-wider opacity-80 truncate">{l}</div>
                      <div className="font-bold text-xs mt-0.5">{ratings[l]}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Your experience */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                  Your experience
                </div>
                <div className="inline-flex w-full rounded-full border border-border bg-background/60 p-0.5 text-[11px] font-bold">
                  {LEVELS.map((l) => (
                    <button
                      key={l}
                      onClick={() => setCrit((s) => ({ ...s, level: l }))}
                      className={cn(
                        "flex-1 min-w-0 truncate rounded-full px-2 py-1.5 transition-all",
                        crit.level === l ? "bg-gradient-sunset text-accent-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* How you surf */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                  How you surf
                </div>
                <div className="inline-flex w-full rounded-full border border-border bg-background/60 p-0.5 text-[11px] font-bold">
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setCrit((prev) => ({ ...prev, style: s }))}
                      className={cn(
                        "flex-1 min-w-0 truncate rounded-full px-2 py-1.5 transition-all",
                        crit.style === s ? "bg-gradient-sunset text-accent-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                    Your weight
                  </div>
                  <div className="text-sm font-bold text-foreground">{crit.weightKg} kg</div>
                </div>
                <Slider
                  value={[crit.weightKg]}
                  min={40}
                  max={120}
                  step={1}
                  onValueChange={(v) => setCrit((s) => ({ ...s, weightKg: v[0] }))}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── WETSUIT TAB ── */}
      {activeTab === "wetsuit" && (
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-4">
            <div className="text-5xl leading-none shrink-0">{wetsuit.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
                <Thermometer className="h-3.5 w-3.5 shrink-0" /> Wetsuit pick
              </div>
              <h3 className="font-display text-fluid-xl text-safe mt-1 text-foreground leading-tight">
                {wetsuit.thickness}
              </h3>
              <p className="text-[11px] text-muted-foreground mt-1">{wetsuit.extras}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">{wetsuit.blurb}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {wetsuit.brands.map((br) => (
              <div
                key={br.name + br.model}
                className="rounded-2xl border border-border bg-background/40 p-4 hover:border-primary/40 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-primary font-bold">{br.name}</div>
                <div className="font-semibold text-foreground mt-0.5 text-sm">{br.model}</div>
                <div className="text-xs text-muted-foreground mt-1">{br.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WAX TAB ── */}
      {activeTab === "wax" && (
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-4">
            <div className="text-5xl leading-none shrink-0">{wax.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
                Wax pick
              </div>
              <h3 className="font-display text-2xl mt-1 text-foreground leading-tight">
                {wax.type} Wax
              </h3>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded-full font-medium">
                  Base: {wax.baseCoat}
                </span>
                <span className="text-xs bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded-full font-medium">
                  Top: {wax.topCoat}
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">{wax.blurb}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {wax.brands.map((br) => (
              <div
                key={br.name + br.model}
                className="rounded-2xl border border-border bg-background/40 p-4 hover:border-primary/40 transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-primary font-bold">{br.name}</div>
                <div className="font-semibold text-foreground mt-0.5 text-sm">{br.model}</div>
                <div className="text-xs text-muted-foreground mt-1">{br.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
